import type { Cohort, SignalsAggregates } from "@/application/signals/types";
import { SignalsCard, EmptyState } from "./SignalsCard";
import { BarList, type BarItem } from "./charts";
import { formatStallStage, titleCase } from "../format";

interface StallRow extends BarItem {
  segment: string;
  segmentLabel: string;
  stage: string;
}

/**
 * Where non-activated merchants are stuck, as segment × stage.
 *
 * Only merchants that have not transacted appear here — an activated merchant
 * has no stall stage — so the counts are the addressable backlog rather than a
 * census of the base.
 */
export function StallMapCard({
  aggregates,
  onDrill,
}: {
  aggregates: SignalsAggregates;
  onDrill: (cohort: Cohort) => void;
}) {
  const labelBySegment = new Map<string, string>(
    aggregates.segments.map((segment) => [segment.segment, segment.label]),
  );

  const rows: StallRow[] = [];
  for (const [segment, stages] of Object.entries(aggregates.stallMap)) {
    for (const [stage, count] of Object.entries(stages)) {
      const segmentLabel = labelBySegment.get(segment) ?? titleCase(segment);
      rows.push({
        key: `${segment}:${stage}`,
        label: `${segmentLabel} · ${formatStallStage(stage)}`,
        value: count,
        segment,
        segmentLabel,
        stage,
        color: stage.startsWith("identity")
          ? "bg-amber-500"
          : stage.startsWith("subscription")
            ? "bg-violet-500"
            : "bg-rose-500",
      });
    }
  }
  rows.sort((a, b) => b.value - a.value);

  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <SignalsCard
      title="Where merchants stall"
      subtitle={
        total > 0
          ? `${total} merchants have not taken a payment`
          : "Every merchant has transacted"
      }
    >
      {rows.length === 0 ? (
        <EmptyState message="Nothing stalled in this build" />
      ) : (
        <BarList
          items={rows}
          onSelect={(item) => {
            const row = item as StallRow;
            onDrill({
              label: `${row.segmentLabel} · ${formatStallStage(row.stage)}`,
              filter: { segment: row.segment, stallStage: row.stage },
            });
          }}
        />
      )}
    </SignalsCard>
  );
}
